import React from 'react';
import styled from 'styled-components';
import styledContainerQuery from 'styled-container-query';
import Link from 'next/link';
import { observer } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import type { GqlSpecialMenuCard, GqlSpecials } from 'types/graphql';

import usePaths from 'src/hooks/use-paths';
import useCart from 'src/hooks/use-cart';
import { Progress } from './progress';

type SpecialCardSpecial = GqlSpecials & Partial<Pick<GqlSpecialMenuCard, 'isRestricted'>>;

type SpecialCardProps = {
  special: SpecialCardSpecial;
};

export const SpecialCard = observer(
  ({ special }: SpecialCardProps): JSX.Element => {
    const { href } = usePaths({ specials: true, id: `offer/${String(special._id)}` });
    const { menuDisplayName, isRestricted } = special;
    const { discountGamificationDetails } = useCart();
    const flags = useFlags();

    // Feature flag check
    const filterCustomerSegmentsEnabled =
      flags['core.cats-ecomm.online-customer-membership-discounts.rollout'] ?? false;

    const matchingCartDiscountGamification = discountGamificationDetails?.find(
      (offer) => offer.specialId === special._id
    );

    const { currentAmount = 0, threshold = 0 } = matchingCartDiscountGamification?.thresholds[0] ?? {};
    const progress = Math.max(0, (currentAmount / threshold) * 100);

    // Show "Members Only" badge for all restricted discounts
    const showBadge = filterCustomerSegmentsEnabled && isRestricted === true;

    return (
      <Link href={href}>
        <Wrapper href={href}>
          <Progress progress={Number.isFinite(progress) ? progress : 0} />

          <Content>
            <NameRow>
              <Name>{menuDisplayName}</Name>
              {showBadge && <RestrictedBadge>Members Only</RestrictedBadge>}
            </NameRow>
            <ShopOffer>Shop Offer</ShopOffer>
          </Content>
        </Wrapper>
      </Link>
    );
  }
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Wrapper = styledContainerQuery.a`
  align-items: center;
  background: #ceeedb;
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  display: flex;
  gap: 12px;
  padding: 16px;
  width: 100%;

  &:container(min-width: 359px) {
    ${Content} {
      align-items: center;
      flex-direction: row;
      gap: 12px;
    }
`;

const Name = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  line-height: ${16 / 14};
  overflow: hidden;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const RestrictedBadge = styled.span`
  background: #00624a;
  border-radius: 4px;
  color: white;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 8px;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ShopOffer = styled.div`
  color: #00624a;
  font-size: 13px;
  font-weight: 600;
  line-height: ${16 / 13};
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;
